/**
 * Where a deck's JSON actually goes when Save is pressed.
 *
 * Historically every save was an anchor click with a `download` attribute: the
 * browser dropped a new numbered file into the Downloads folder and the app
 * never learned where the deck lived. Saving the same deck five times left five
 * files, none of them where the instructor keeps their course material.
 *
 * Browsers with the File System Access API can do better, so this module keeps
 * two handles for the open deck:
 *
 *   - a **file** handle — the file Save overwrites, remembered from the last
 *     save-as or the last file the user pointed at;
 *   - a **directory** handle — the folder "Save +" writes the next version
 *     into, set explicitly with "Set Dir" or picked up from a save-as.
 *
 * Handles are structured-cloneable, so they persist in IndexedDB and survive a
 * reload. They do NOT persist their permission grant: after a reload the first
 * write has to ask again, which is why every write goes through
 * `ensureWritePermission` and quietly falls back to a picker if it is refused.
 *
 * Firefox and Safari have no File System Access API at all. There `saveDeckJson`
 * returns to the old anchor download, so nothing regresses — it simply cannot
 * remember a location.
 */

const DB_NAME = 'lessonplannerone-save-location';
const DB_VERSION = 1;
const STORE_NAME = 'handles';

export const FILE_HANDLE_KEY = 'deck-file';
export const DIRECTORY_HANDLE_KEY = 'deck-directory';

/** How a save was carried out — reported back so the UI can say something true. */
export const SaveMethod = {
  /** Rewrote the remembered file in place. */
  OVERWRITE: 'overwrite',
  /** Created (or replaced) a file inside the remembered directory. */
  DIRECTORY: 'directory',
  /** The user chose the destination in the browser's save dialog. */
  PICKER: 'picker',
  /** No File System Access API: fell back to a plain download. */
  DOWNLOAD: 'download'
};

export const JSON_PICKER_TYPES = [
  {
    description: 'Lesson deck JSON',
    accept: { 'application/json': ['.json'] }
  }
];

/** True when this browser can be pointed at a specific file to overwrite. */
export function supportsFileSystemAccess() {
  return typeof window !== 'undefined' && typeof window.showSaveFilePicker === 'function';
}

/** True when this browser can hand back a folder to write into. */
export function supportsDirectoryPicker() {
  return typeof window !== 'undefined' && typeof window.showDirectoryPicker === 'function';
}

/** A cancelled picker is not a failure — the user simply changed their mind. */
export function isAbortError(error) {
  return Boolean(error) && (error.name === 'AbortError' || error.code === 20);
}

/* ------------------------------------------------------------------ *
 * Handle persistence (IndexedDB)
 *
 * localStorage cannot hold a file handle — it only stores strings, and a
 * handle is an object the browser has to clone. IndexedDB can, so a deck
 * opened yesterday still knows where it is saved today.
 * ------------------------------------------------------------------ */

function openHandleDb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      resolve(null);
      return;
    }
    let request;
    try {
      request = indexedDB.open(DB_NAME, DB_VERSION);
    } catch (error) {
      resolve(null);
      return;
    }
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
    // Private-mode Firefox and some locked-down profiles never settle the
    // request; treating that as "no store" is better than hanging the save.
    request.onblocked = () => resolve(null);
  });
}

async function withStore(mode, run) {
  let db = null;
  try {
    db = await openHandleDb();
  } catch (error) {
    return null;
  }
  if (!db) return null;

  try {
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, mode);
      const store = tx.objectStore(STORE_NAME);
      const request = run(store);
      tx.oncomplete = () => resolve(request ? request.result : undefined);
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  } catch (error) {
    console.warn('[saveLocation] Handle store unavailable', error);
    return null;
  } finally {
    db.close();
  }
}

export function readStoredHandle(key) {
  return withStore('readonly', (store) => store.get(key));
}

export function writeStoredHandle(key, value) {
  return withStore('readwrite', (store) => store.put(value, key));
}

export function deleteStoredHandle(key) {
  return withStore('readwrite', (store) => store.delete(key));
}

/**
 * Read back both remembered handles.
 *
 * Anything that is not a live handle (a stale record from an older build, a
 * revoked handle) is reported as absent rather than thrown, so a corrupt entry
 * costs a picker prompt instead of a broken Save button.
 */
export async function loadSaveLocation() {
  const [fileHandle, directoryHandle] = await Promise.all([
    readStoredHandle(FILE_HANDLE_KEY).catch(() => null),
    readStoredHandle(DIRECTORY_HANDLE_KEY).catch(() => null)
  ]);

  return {
    fileHandle: isFileHandle(fileHandle) ? fileHandle : null,
    directoryHandle: isDirectoryHandle(directoryHandle) ? directoryHandle : null
  };
}

export async function clearSaveLocation() {
  await Promise.all([
    deleteStoredHandle(FILE_HANDLE_KEY).catch(() => null),
    deleteStoredHandle(DIRECTORY_HANDLE_KEY).catch(() => null)
  ]);
}

function isFileHandle(handle) {
  return Boolean(handle) && handle.kind === 'file' && typeof handle.createWritable === 'function';
}

function isDirectoryHandle(handle) {
  return Boolean(handle) && handle.kind === 'directory' && typeof handle.getFileHandle === 'function';
}

/**
 * Is this remembered handle the file the caller is trying to write?
 *
 * Compared by name, because that is all a handle exposes and it is what
 * changes when the deck does: a different deck has a different base name, and
 * a different version has a different suffix. Case-insensitive, because
 * Windows and macOS both treat `Deck_v3.json` and `deck_v3.json` as one file
 * and a case-sensitive check here would quietly create a second one.
 */
export function handleTargets(handle, fileName) {
  if (!isFileHandle(handle) || !fileName) return false;
  return String(handle.name || '').toLowerCase() === String(fileName).toLowerCase();
}

/**
 * Would saving under this name replace something that is already there?
 *
 * Asked before a plain "Save" so the user can be warned that a numbered
 * version is about to be rewritten — and, just as importantly, NOT warned when
 * the save is creating a file for the first time.
 *
 * Only ever answers "yes" when it can see the file. A browser without the File
 * System Access API, an unset location, or a folder that refuses to be read all
 * answer "no": those saves go through the browser's own dialog or the Downloads
 * folder, both of which handle a collision themselves.
 *
 * @returns {Promise<boolean>}
 */
export async function targetFileExists({ fileName, fileHandle = null, directoryHandle = null } = {}) {
  if (!fileName || !supportsFileSystemAccess()) return false;
  if (handleTargets(fileHandle, fileName)) return true;
  if (!isDirectoryHandle(directoryHandle)) return false;
  // Permission is checked without prompting: this runs before the user has
  // agreed to anything, and a dialog here would be a dialog about a dialog.
  if (!(await ensureWritePermission(directoryHandle, { prompt: false }))) return false;
  try {
    await directoryHandle.getFileHandle(fileName, { create: false });
    return true;
  } catch (error) {
    // NotFoundError is the answer, not a problem. Anything else means the
    // folder could not be read, and an unreadable folder is not evidence of a
    // file to overwrite.
    return false;
  }
}

/* ------------------------------------------------------------------ *
 * Permissions and writing
 * ------------------------------------------------------------------ */

/**
 * Make sure a handle may be written to, asking the user if the browser wants
 * it re-confirmed (which it does after every reload).
 *
 * @param {FileSystemHandle | null} handle
 * @param {{ prompt?: boolean }} [options] - `prompt: false` only checks, so a
 *   menu can show the current location without throwing a dialog at anyone.
 * @returns {Promise<boolean>}
 */
export async function ensureWritePermission(handle, { prompt = true } = {}) {
  if (!handle) return false;
  const descriptor = { mode: 'readwrite' };

  try {
    if (typeof handle.queryPermission === 'function') {
      const state = await handle.queryPermission(descriptor);
      if (state === 'granted') return true;
      if (state === 'denied') return false;
    }
    if (!prompt || typeof handle.requestPermission !== 'function') return false;
    return (await handle.requestPermission(descriptor)) === 'granted';
  } catch (error) {
    console.warn('[saveLocation] Permission check failed', error);
    return false;
  }
}

/* ------------------------------------------------------------------ *
 * Writing, and proving the write landed
 *
 * Chromium does not write through a handle directly. `createWritable()`
 * opens a sibling `<name>.crswap` file, writes there, runs a Safe Browsing
 * check over it, and only then renames it over the target. On a local disk
 * that rename is free and invisible.
 *
 * On a Google Drive for Desktop volume it is neither. Drive's filesystem
 * driver is a virtual one: the swap file is a new unknown-extension file it
 * has to sync, the rename-over-existing is an operation it handles badly
 * (it is known to resolve rename collisions by inventing "name (1).json"),
 * and the Safe Browsing pass on a cloud path has been measured in minutes on
 * Windows. Any of those turns "Save" into "nothing appeared in the folder"
 * with no error thrown at all — the close() resolved, so as far as the page
 * was concerned it worked.
 *
 * Two defences, in order:
 *   1. `mode: 'exclusive'` (Chrome 121+) skips the per-writer swap file, so
 *      most of the above never happens. Older browsers ignore the unknown
 *      option, and a browser that rejects it falls back to the default.
 *   2. Read the file back and compare its size. A write that did not land is
 *      then a failure the app can talk about, instead of a silence.
 * ------------------------------------------------------------------ */

/** Why a save could not be completed — the UI picks its wording from this. */
export const SaveFailureReason = {
  /** The browser or OS refused the write outright. */
  WRITE_FAILED: 'write-failed',
  /** The write reported success but the file on disk does not match. */
  NOT_VERIFIED: 'not-verified',
  /** A folder that cannot be written to at all. */
  FOLDER_UNUSABLE: 'folder-unusable'
};

export class SaveLocationError extends Error {
  constructor(reason, message, cause = null, details = {}) {
    super(message);
    this.name = 'SaveLocationError';
    this.reason = reason;
    this.cause = cause;
    this.details = details;
  }
}

/**
 * Folder names that mean "this is a cloud-sync volume, not a disk".
 *
 * Only ever used to sharpen a message that is already being shown — the
 * failure is detected by what happened, never by the name. A handle exposes
 * its leaf name and nothing else, so this cannot be a test for anything.
 */
const CLOUD_FOLDER_HINTS = [
  'my drive',
  'google drive',
  'shared drives',
  'shared with me',
  'onedrive',
  'dropbox',
  'icloud drive'
];

export function looksLikeCloudFolder(name) {
  if (!name) return false;
  const lower = String(name).toLowerCase();
  return CLOUD_FOLDER_HINTS.some((hint) => lower === hint || lower.includes(hint));
}

const wait = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

/**
 * Open a writable stream, preferring the mode that does not use a swap file.
 *
 * `mode` is ignored by browsers that predate it (unknown dictionary members
 * are dropped), so this is safe to send everywhere. A browser that actively
 * refuses it, or a file another tab already holds exclusively, falls back to
 * the default writer rather than failing the save.
 */
async function openWritable(handle) {
  try {
    return await handle.createWritable({ mode: 'exclusive' });
  } catch (error) {
    if (error?.name === 'NotAllowedError' || error?.name === 'NotFoundError') throw error;
    return handle.createWritable();
  }
}

/**
 * Read the file back and check it is the size we just wrote.
 *
 * Retried a couple of times: a cloud volume can take a moment to publish the
 * renamed file, and calling that a failure on the first look would cry wolf
 * over a save that was merely slow.
 */
export async function verifyWrittenFile(handle, expectedBytes, { attempts = 3, delayMs = 250 } = {}) {
  let lastSize = null;
  let lastError = null;

  for (let attempt = 0; attempt < attempts; attempt += 1) {
    try {
      const file = await handle.getFile();
      if (file.size === expectedBytes) return { ok: true, size: file.size };
      lastSize = file.size;
    } catch (error) {
      lastError = error;
    }
    if (attempt < attempts - 1) await wait(delayMs * (attempt + 1));
  }

  return { ok: false, size: lastSize, error: lastError };
}

/**
 * Write text through a file handle, replacing whatever was there, and confirm
 * it actually arrived.
 *
 * @throws {SaveLocationError} when the write fails, or when it claims to have
 *   succeeded and the file says otherwise
 */
export async function writeTextToHandle(handle, text, { verify = {} } = {}) {
  const blob = text instanceof Blob ? text : new Blob([text], { type: 'application/json' });

  try {
    const writable = await openWritable(handle);
    try {
      await writable.write(blob);
    } finally {
      // close() is where the swap file is renamed over the target, so it is
      // where a cloud volume usually throws. It has to be awaited, and its
      // failure has to travel.
      await writable.close();
    }
  } catch (error) {
    throw new SaveLocationError(
      SaveFailureReason.WRITE_FAILED,
      error?.message || 'The file could not be written',
      error,
      { fileName: handle?.name || '' }
    );
  }

  const verified = await verifyWrittenFile(handle, blob.size, verify);
  if (!verified.ok) {
    throw new SaveLocationError(
      SaveFailureReason.NOT_VERIFIED,
      'The browser reported the file as saved, but the file on disk does not match',
      verified.error || null,
      { fileName: handle?.name || '', expectedBytes: blob.size, actualBytes: verified.size }
    );
  }
}

/** The name a folder probe writes and then removes. */
export const PROBE_FILE_NAME = '.lessonplannerone-write-test';

/**
 * Find out whether a folder can really be written to, at the moment it is
 * chosen rather than at the moment the deck is due to be saved.
 *
 * Writes a few bytes, reads them back, deletes them. Google Drive folders are
 * the reason this exists: they accept being picked, then swallow writes.
 *
 * @returns {Promise<{ ok: boolean, error: SaveLocationError | null }>}
 */
export async function probeDirectory(directoryHandle, { verify } = {}) {
  if (!isDirectoryHandle(directoryHandle)) {
    return {
      ok: false,
      error: new SaveLocationError(SaveFailureReason.FOLDER_UNUSABLE, 'That is not a folder')
    };
  }

  try {
    const probe = await directoryHandle.getFileHandle(PROBE_FILE_NAME, { create: true });
    await writeTextToHandle(probe, 'lessonplannerone', { verify });
    return { ok: true, error: null };
  } catch (error) {
    const wrapped = error instanceof SaveLocationError
      ? error
      : new SaveLocationError(SaveFailureReason.FOLDER_UNUSABLE, error?.message || 'The folder could not be written to', error);
    wrapped.details = { ...(wrapped.details || {}), folderName: directoryHandle.name || '' };
    return { ok: false, error: wrapped };
  } finally {
    // Best effort: a probe file left behind is untidy, not harmful, and a
    // folder that refuses the delete has already told us what we came to find.
    try {
      await directoryHandle.removeEntry(PROBE_FILE_NAME);
    } catch (error) {
      /* nothing to do */
    }
  }
}

/** The old behaviour, kept for browsers without the File System Access API. */
export function downloadTextFile(text, fileName, mimeType = 'application/json') {
  const blob = new Blob([text], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  // Attached for the click: Firefox ignores one on an anchor that is not in
  // the document, which is the same rule the file pickers live under.
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

/**
 * Ask the user where to save, starting in the remembered folder when there is
 * one so the dialog opens where the deck already lives.
 *
 * @returns {Promise<FileSystemFileHandle | null>} null when the dialog was dismissed
 */
export async function chooseSaveFile({ suggestedName, startIn = null } = {}) {
  if (!supportsFileSystemAccess()) return null;
  try {
    return await window.showSaveFilePicker({
      suggestedName,
      types: JSON_PICKER_TYPES,
      ...(startIn ? { startIn } : {})
    });
  } catch (error) {
    if (isAbortError(error)) return null;
    throw error;
  }
}

/**
 * Ask the user for the folder this deck belongs in — the "Set Dir" button.
 *
 * @returns {Promise<FileSystemDirectoryHandle | null>} null when dismissed
 */
export async function chooseDirectory({ startIn = null } = {}) {
  if (!supportsDirectoryPicker()) return null;
  try {
    return await window.showDirectoryPicker({
      mode: 'readwrite',
      ...(startIn ? { startIn } : {})
    });
  } catch (error) {
    if (isAbortError(error)) return null;
    throw error;
  }
}

/**
 * Write the deck's JSON to the right place.
 *
 * The decision, in order:
 *
 *   1. No File System Access API → download it, same as always.
 *   2. "Save" (`newFile: false`) with a remembered file **of the same name** →
 *      overwrite that file. This is the case the whole module exists for:
 *      press Save twice and you have one file, not two.
 *   3. A remembered directory → write `fileName` into it. "Save +" always lands
 *      here when a directory is known, which is how a version-up produces
 *      `deck_v4.json` next to `deck_v3.json` without another dialog.
 *   4. Otherwise → open the save dialog, and remember what comes back so step 2
 *      applies next time.
 *
 * The "of the same name" in step 2 is load-bearing. A remembered handle is
 * global to the app, not to the deck: it survives a reload, and it survives
 * "+ New" and opening a different file. Without the name check, the first Save
 * of a *new* deck went straight into the previous deck's file — the new deck's
 * `_v1.json` was never created, and the old deck was destroyed, both silently.
 * "Save +" appeared to be the fix only because it skips this step entirely.
 *
 * Handles are returned rather than stored here; the caller decides what to
 * remember, which keeps this function testable without IndexedDB.
 *
 * @param {object} options
 * @param {string} options.text - The serialized JSON.
 * @param {string} options.fileName - File name to use, with extension.
 * @param {boolean} [options.newFile] - True for "Save +": never overwrite the
 *   remembered file, because the point is to sit alongside it.
 * @param {FileSystemFileHandle | null} [options.fileHandle]
 * @param {FileSystemDirectoryHandle | null} [options.directoryHandle]
 * @param {boolean} [options.allowPicker] - False suppresses the dialog, for a
 *   save that must not interrupt (autosave-style callers).
 * @param {object} [options.verify] - Read-back settings, for tests that do not
 *   want to wait out the retry delays.
 * @returns {Promise<{ method: string, fileName: string, cancelled: boolean,
 *   fileHandle: FileSystemFileHandle | null,
 *   directoryHandle: FileSystemDirectoryHandle | null }>}
 * @throws {SaveLocationError} When the write fails or cannot be verified. A
 *   save that did not happen must never look like one that did.
 */
export async function saveDeckJson({
  text,
  fileName,
  newFile = false,
  fileHandle = null,
  directoryHandle = null,
  allowPicker = true,
  verify
}) {
  const result = {
    method: SaveMethod.DOWNLOAD,
    fileName,
    cancelled: false,
    fileHandle: null,
    directoryHandle: directoryHandle || null
  };

  if (!supportsFileSystemAccess()) {
    downloadTextFile(text, fileName);
    return result;
  }

  // 2. Overwrite the file this deck was last saved to — but only if the
  //    remembered file is the one this save is aimed at.
  if (!newFile && handleTargets(fileHandle, fileName) && (await ensureWritePermission(fileHandle))) {
    await writeTextToHandle(fileHandle, text, { verify });
    return {
      ...result,
      method: SaveMethod.OVERWRITE,
      fileName: fileHandle.name || fileName,
      fileHandle
    };
  }

  // 3. Write into the folder the user designated.
  if (isDirectoryHandle(directoryHandle) && (await ensureWritePermission(directoryHandle))) {
    const handle = await directoryHandle.getFileHandle(fileName, { create: true });
    try {
      await writeTextToHandle(handle, text, { verify });
    } catch (error) {
      // Name the folder on the way past: it is the thing the message needs to
      // be about, and only this branch knows it.
      if (error instanceof SaveLocationError) {
        error.details = { ...(error.details || {}), folderName: directoryHandle.name || '' };
      }
      throw error;
    }
    return {
      ...result,
      method: SaveMethod.DIRECTORY,
      fileName: handle.name || fileName,
      fileHandle: handle,
      directoryHandle
    };
  }

  // 4. Ask.
  if (!allowPicker) {
    downloadTextFile(text, fileName);
    return result;
  }

  // A file handle is a valid `startIn` too, and it points at the folder that
  // file lives in — so "Save +" with no folder set still opens the dialog
  // beside the version it is following.
  const chosen = await chooseSaveFile({
    suggestedName: fileName,
    startIn: (isDirectoryHandle(directoryHandle) && directoryHandle)
      || (isFileHandle(fileHandle) && fileHandle)
      || null
  });
  if (!chosen) {
    return { ...result, cancelled: true, method: SaveMethod.PICKER };
  }

  await writeTextToHandle(chosen, text, { verify });
  return {
    ...result,
    method: SaveMethod.PICKER,
    fileName: chosen.name || fileName,
    fileHandle: chosen
  };
}

/**
 * Explain a save that did not happen, in terms of the folder it was aimed at.
 *
 * The Google Drive case gets named explicitly. It is far and away the most
 * common way this fails, the symptom (nothing appears, no error) gives no
 * clue on its own, and "your cloud folder is the problem" is not a guess
 * someone makes unprompted about a Save button.
 *
 * @param {unknown} error
 * @param {{ folderName?: string, fileName?: string }} [context]
 * @returns {{ title: string, summary: string, detail: string, suggestions: string[], cloudFolder: boolean }}
 */
export function describeSaveProblem(error, context = {}) {
  const details = (error && error.details) || {};
  const folderName = context.folderName || details.folderName || '';
  const fileName = context.fileName || details.fileName || '';
  const cloudFolder = looksLikeCloudFolder(folderName);
  const where = folderName ? `“${folderName}”` : 'that folder';

  const cloudAdvice = [
    'Save to a folder on the computer’s own disk — Documents, or the desktop — and let the sync app copy it up afterwards.',
    'Google Drive, OneDrive and Dropbox folders are not real disks: the browser writes a temporary file and renames it into place, and a sync driver can lose or rename that step without reporting an error.',
    'Use "Download instead" below to get the file now, then move it wherever you like.'
  ];

  if (error?.reason === SaveFailureReason.NOT_VERIFIED) {
    return {
      title: 'That save did not reach the disk',
      summary: `The browser reported ${fileName || 'the file'} as saved, but reading it back from ${where} gave something different${
        typeof details.actualBytes === 'number' && typeof details.expectedBytes === 'number'
          ? ` (${details.actualBytes} bytes on disk against ${details.expectedBytes} written)`
          : ''
      }. Your deck has NOT been saved.`,
      detail: error.message || '',
      suggestions: cloudFolder
        ? cloudAdvice
        : [
          'Pick a different folder and try again — this one is not accepting writes.',
          'A folder synced by Google Drive, OneDrive or Dropbox is the usual cause; a folder on the computer’s own disk is not.',
          'Use "Download instead" below to get the file now.'
        ],
      cloudFolder
    };
  }

  if (error?.reason === SaveFailureReason.FOLDER_UNUSABLE) {
    return {
      title: 'That folder cannot be saved into',
      summary: `A test write to ${where} did not survive being read back, so saving a deck there would appear to work and quietly do nothing.`,
      detail: error.message || '',
      suggestions: cloudFolder ? cloudAdvice : [
        'Choose a folder on the computer’s own disk instead.',
        'Cloud-sync folders (Google Drive, OneDrive, Dropbox) and network drives are the usual culprits.',
        'Whatever you choose, the deck itself is untouched and still open.'
      ],
      cloudFolder
    };
  }

  const cause = error?.cause;
  if (cause?.name === 'NoModificationAllowedError') {
    return {
      title: 'That file is locked by something else',
      summary: `${fileName || 'The file'} could not be written because another program is holding it open.`,
      detail: cause.message || error?.message || '',
      suggestions: [
        'Close the file anywhere else it is open, then save again.',
        'A sync client mid-upload also holds files — wait a moment and retry.',
        'Or save under a different name with "Save as…".'
      ],
      cloudFolder
    };
  }

  if (cause?.name === 'NotFoundError') {
    return {
      title: 'That file is no longer where it was',
      summary: `${fileName || 'The file'} has been moved, renamed or deleted since it was last saved, so there was nothing to write to.`,
      detail: cause.message || '',
      suggestions: [
        'Choose the folder or file again — the app will remember the new one.',
        'A sync client that renames files (Drive turns a clash into "name (1).json") does this.',
        'Use "Download instead" below if you just want the file now.'
      ],
      cloudFolder
    };
  }

  return {
    title: 'That deck could not be saved',
    summary: `Writing ${fileName || 'the file'} to ${where} failed, so nothing was saved. Your work is still open and unchanged.`,
    detail: error?.message || String(error || ''),
    suggestions: cloudFolder ? cloudAdvice : [
      'Try a different folder — one on the computer’s own disk.',
      'Check the folder still exists and is not read-only.',
      'Use "Download instead" below to get the file now.'
    ],
    cloudFolder
  };
}

/**
 * One line describing where Save will write, for the Save menu.
 *
 * @returns {string}
 */
export function describeSaveLocation({ fileHandle = null, directoryHandle = null } = {}) {
  if (!supportsFileSystemAccess()) return 'Downloads folder';
  if (isFileHandle(fileHandle) && isDirectoryHandle(directoryHandle)) {
    return `${directoryHandle.name}/${fileHandle.name}`;
  }
  if (isFileHandle(fileHandle)) return fileHandle.name;
  if (isDirectoryHandle(directoryHandle)) return `${directoryHandle.name}/`;
  return 'Not set — Save will ask';
}
